/**
 * Las decisiones de "se cerró la sesión", separadas de la pantalla que las muestra.
 *
 * Están acá y no dentro del componente por una razón práctica: las vistas React no se
 * prueban en este paquete (el proyecto `web` corre sin DOM). Metidas en el `.tsx`, la
 * parte que de verdad puede equivocarse —cuál 401 es una sesión caída, y a dónde es
 * seguro devolver a la persona— quedaría sin una sola prueba.
 */

/** Dónde se deja la dirección para volver después de entrar. */
export const CLAVE_VOLVER_A = 'supernova:volver-a';

/**
 * ¿Este error es "se te cerró la sesión"?
 *
 * Solo los 401 que emite el portero. `INVALID_CREDENTIALS` es el 401 de una contraseña
 * equivocada y lo muestra el propio formulario de ingreso: tratarlo como sesión caída
 * taparía el formulario con un aviso de "volvé a entrar" a alguien que justamente está
 * intentando entrar.
 */
export function esSesionQueSeCerro(status: number, code: unknown): boolean {
  if (status !== 401) return false;
  // SESSION_OTHER_PRODUCT: la sesión es de OPUS ai y se está usando ci (o al revés).
  // SESSION_REVOKED: el servidor cerró la sesión (cerró sesión en otro lado, se abrió otra
  // más allá del límite, o la revocó soporte).
  return code === 'UNAUTHENTICATED' || code === 'INVALID_TOKEN' || code === 'SESSION_OTHER_PRODUCT'
    || code === 'SESSION_REVOKED';
}

/**
 * A dónde va la persona apenas entra.
 *
 * Normalmente al inicio; pero si llegó al ingreso porque se le cerró la sesión en medio
 * del trabajo, vuelve a la pantalla en la que estaba. Perder la sesión no debería costar
 * además reencontrar dónde estabas.
 *
 * Solo se acepta una ruta de esta aplicación. Una dirección absoluta —o una que empiece
 * con `//`, que el navegador entiende como otro sitio— sería un salto afuera disfrazado
 * de regreso: basta con que alguien consiga escribir esa clave para mandar a la persona
 * a una pantalla de ingreso ajena, recién salida de una de verdad.
 */
export function rutaDeRegresoValida(guardada: string | null | undefined): string {
  if (!guardada) return '/';
  if (!guardada.startsWith('/')) return '/'; // absoluta, o relativa suelta
  if (guardada.startsWith('//')) return '/'; // "//otro-sitio.com" es otro sitio
  if (guardada.startsWith('/\\')) return '/'; // "/\" lo leen algunos navegadores como //
  return guardada;
}

/** Lo mínimo que se necesita de `sessionStorage`; así se puede probar sin navegador. */
export interface AlmacenDeSesion {
  getItem(clave: string): string | null;
  removeItem(clave: string): void;
}

/**
 * Toma la ruta de regreso y la consume, pero **recordando la respuesta**: llamar dos
 * veces devuelve lo mismo.
 *
 * Ese "recordando" no es un lujo. La primera versión leía y borraba en cada llamada, y
 * en el navegador la persona terminaba en el inicio y no donde estaba: React llama al
 * efecto dos veces en desarrollo, la primera se llevaba la dirección y la segunda ya
 * encontraba vacío. Cualquier lectura repetida —un re-render, un reintento— rompía lo
 * mismo. Se guarda la dirección al lado de quien pregunta (el `recuerdo`), y el
 * almacenamiento se limpia una sola vez.
 */
export function tomarRutaDeRegreso(
  almacen: AlmacenDeSesion | null | undefined,
  recuerdo: { valor: string | null },
): string {
  if (recuerdo.valor !== null) return recuerdo.valor;
  try {
    const guardada = almacen?.getItem(CLAVE_VOLVER_A) ?? null;
    almacen?.removeItem(CLAVE_VOLVER_A);
    recuerdo.valor = rutaDeRegresoValida(guardada);
  } catch {
    recuerdo.valor = '/'; // sin almacenamiento: al inicio, como siempre
  }
  return recuerdo.valor;
}

/**
 * ¿El 401 que acaba de llegar es "se cerró tu sesión" o "todavía no entraste"?
 *
 * SE DECIDE EN EL MOMENTO EN QUE LLEGA, y esto no es un detalle: la primera versión
 * prendía el aviso con cualquier 401 y lo tapaba al dibujar preguntando si había sesión.
 * El resultado, visto en pruebas el 24/08: al abrir la aplicación sin haber entrado, el
 * saludo normal del motor —un 401 `UNAUTHENTICATED` a "¿quién sos?"— dejaba el aviso
 * prendido a la espera; y en cuanto la persona entraba, el candado se abría y lo primero
 * que veía después de un ingreso exitoso era "Tu sesión se cerró".
 *
 * Un 401 solo significa que la sesión se cerró si HABÍA una sesión abierta cuando llegó.
 */
export function esAvisoDeSesionQueSeCerro(statusAlLlegarEl401: string): boolean {
  return statusAlLlegarEl401 === 'authenticated';
}
